import UserInterfaceUserGroup from '@mds/icons/Users';
import Link from 'components/ui/AppLink';
import Loader from 'components/ui/LoadingIndicator';
import { Box } from '@mui/material';
import React from 'react';
import { MessageView } from 'components/ui/Feedback';
import ArticlePreviewList from '../../ui/ArticlePreviewList';
import { Grid } from '@mui/material';
import Contributor from '../../ui/Contributor';
import { useActivityLogger } from '../../hooks/use-activity-logger';
import ChannelPreviewList from '../../ui/ChannelPreviewList';
import { DESK_PAGE } from '../../../gql/desks';
import { sortArrayByPropertyAlphabetically } from '../../../utils/sorting.utils';
import SectionHeaderSmall from '../../ui/SectionHeaderSmall';
import { sanitizeRouteId } from '../../../utils/route-params.utils';
import { isValidMongoId } from '../../../utils/id-validation.utils';

import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { Desk, User } from '../../../shared/types';

const DeskPage = () => {
    const { push } = useHistory();
    const { _id: routeId } = useParams<{ _id?: string }>();
    const _id = sanitizeRouteId(routeId);
    const isValidDeskId = isValidMongoId(_id);

    const errorMessage = (
        <MessageView
            styleName="primary"
            size="large"
            title="Desk not found"
            primaryAction={{
                label: 'Return to Neon Insights',
                onClick: () => push('/'),
            }}
        />
    );

    const { data, loading } = useQuery<{ document: Desk }>(DESK_PAGE, {
        skip: !isValidDeskId,
        fetchPolicy: 'network-only',
        variables: { id: _id },
    });

    const desk = data?.document;
    const deskName = desk?.name || '';
    const deskEmail = desk?.email || '';

    useActivityLogger('Desk', deskName, _id);
    if (!isValidDeskId) return errorMessage;
    if (loading) return <Loader />;
    if (!desk) return errorMessage;

    const contributors = sortArrayByPropertyAlphabetically(
        desk?.contributors || [],
        'name'
    );
    const channels = sortArrayByPropertyAlphabetically(
        desk?.channels || [],
        'name'
    );

    return (
        <Grid container spacing={8}>
            <Grid size={12}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                    }}
                >
                    <Box
                        sx={{
                            height: 0,
                            bgcolor: 'primary.main',
                            borderRadius: '50%',
                            minHeight: 64,
                            minWidth: 64,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mr: 2,
                            '& > svg': {
                                color: 'primary.contrastText',
                                width: 28,
                                height: 24,
                            },
                        }}
                    >
                        <UserInterfaceUserGroup fontSize="medium" />
                    </Box>
                    <SectionHeaderSmall
                        large
                        title={deskName}
                        subtitle={
                            <>
                                For general inquiries, please contact
                                <Link
                                    ignoreRouter
                                    to={`mailto:${deskEmail}`}
                                    sx={{ fontSize: 'inherit' }}
                                >
                                    &nbsp;{deskEmail}
                                </Link>
                            </>
                        }
                    />
                </Box>
            </Grid>
            <Grid size={12}>
                <SectionHeaderSmall
                    title="Team contributors"
                    subtitle="All the people that contribute to writing articles"
                />
                <Grid container spacing={2}>
                    {contributors.map((c: User) => (
                        <Grid
                            key={c._id}
                            size={{
                                xs: 12,
                                sm: 6,
                                md: 4,
                                lg: 3,
                            }}
                        >
                            <Contributor
                                _id={c._id}
                                name={c.name}
                                jobFunction={c.position}
                                phone={c.phone}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Grid>
            <Grid size={12}>
                <SectionHeaderSmall title={`Channels curated by ${deskName}`} />
                <ChannelPreviewList channels={channels} loading={loading} />
            </Grid>
            <Grid size={12}>
                <SectionHeaderSmall
                    title={`Articles ${deskName} has contributed to`}
                    subtitle="All the articles published by this Desk"
                />
                <ArticlePreviewList deskId={_id} source="Desk Page" />
            </Grid>
        </Grid>
    );
};

export default DeskPage;
